// Linear-style Cmd+K menu. The list of commands grows as we add features —
// for v1 it has theme controls and navigation; Phase 4 adds task-trigger
// and history-jump entries.
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Moon,
  Sun,
  Monitor,
  Home,
  History,
  Settings,
  PlayCircle,
  Users,
  Server,
  FolderKanban,
  KeyRound,
  Unlock,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useTheme } from "@/lib/theme";

type Ctx = { open: () => void; close: () => void; isOpen: boolean };
const CommandMenuContext = createContext<Ctx | undefined>(undefined);

export function CommandMenuProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const { setTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const ctx = useMemo<Ctx>(
    () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      isOpen,
    }),
    [isOpen]
  );

  function go(path: string) {
    navigate(path);
    setIsOpen(false);
  }

  return (
    <CommandMenuContext.Provider value={ctx}>
      {children}
      <CommandDialog open={isOpen} onOpenChange={setIsOpen}>
        <CommandInput placeholder="Type a command or search…" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigate">
            <CommandItem onSelect={() => go("/")}>
              <Home className="mr-2 h-4 w-4" /> Home
              <CommandShortcut>G H</CommandShortcut>
            </CommandItem>
            <CommandItem onSelect={() => go("/playground")}>
              <PlayCircle className="mr-2 h-4 w-4" /> Playground
              <CommandShortcut>G P</CommandShortcut>
            </CommandItem>
            <CommandItem onSelect={() => go("/history")}>
              <History className="mr-2 h-4 w-4" /> History
            </CommandItem>
            <CommandItem onSelect={() => go("/god")}>
              <Unlock className="mr-2 h-4 w-4" /> God dashboard
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Admin">
            <CommandItem onSelect={() => go("/admin/projects")}>
              <FolderKanban className="mr-2 h-4 w-4" /> Projects
            </CommandItem>
            <CommandItem onSelect={() => go("/admin/users")}>
              <Users className="mr-2 h-4 w-4" /> Users
            </CommandItem>
            <CommandItem onSelect={() => go("/admin/runners")}>
              <Server className="mr-2 h-4 w-4" /> Runners
            </CommandItem>
            <CommandItem onSelect={() => go("/admin/api-keys")}>
              <KeyRound className="mr-2 h-4 w-4" /> API Keys
            </CommandItem>
            <CommandItem onSelect={() => go("/settings")}>
              <Settings className="mr-2 h-4 w-4" /> Settings
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Theme">
            <CommandItem
              onSelect={() => {
                setTheme("light");
                setIsOpen(false);
              }}
            >
              <Sun className="mr-2 h-4 w-4" /> Light
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setTheme("dark");
                setIsOpen(false);
              }}
            >
              <Moon className="mr-2 h-4 w-4" /> Dark
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setTheme("system");
                setIsOpen(false);
              }}
            >
              <Monitor className="mr-2 h-4 w-4" /> System
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </CommandMenuContext.Provider>
  );
}

export function useCommandMenu() {
  const ctx = useContext(CommandMenuContext);
  if (!ctx) throw new Error("useCommandMenu must be used within CommandMenuProvider");
  return ctx;
}
